import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus, Send, ChevronRight, RefreshCw, Inbox, CheckCircle2, AlertCircle, FileEdit, Save, FileClock, X } from "lucide-react";
import { toast } from "sonner";
import { useAppConfig } from "@/lib/AppConfigContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Orders() {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null); // order pending deletion
  const [confirmSendDraft, setConfirmSendDraft] = useState(null);
  const navigate = useNavigate();

  const load = () => Promise.all([api.listOrders(), api.listSuppliers()]).then(([o, s]) => { setItems(o); setSuppliers(s); });
  useEffect(() => { load(); }, []);

  const filtered = items.filter((o) => {
    if (filter !== "all" && o.status !== filter) return false;
    if (supplierFilter !== "all" && o.supplier_id !== supplierFilter) return false;
    if (dateFrom) {
      const d = o.created_at?.slice(0, 10);
      if (!d || d < dateFrom) return false;
    }
    if (dateTo) {
      const d = o.created_at?.slice(0, 10);
      if (!d || d > dateTo) return false;
    }
    return true;
  });

  const clearFilters = () => {
    setSupplierFilter("all"); setDateFrom(""); setDateTo("");
  };
  const hasActiveFilters = supplierFilter !== "all" || dateFrom || dateTo;

  const resend = async (id) => {
    try {
      await api.resendOrder(id);
      toast.success("Pedido reenviado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al reenviar");
    }
  };

  const doSendDraft = async () => {
    const order = confirmSendDraft;
    setConfirmSendDraft(null);
    if (!order) return;
    try {
      await api.sendOrder(order.id);
      toast.success("Pedido enviado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar");
    }
  };

  const doDelete = async () => {
    const order = confirmDelete;
    setConfirmDelete(null);
    if (!order) return;
    try {
      await api.deleteOrder(order.id);
      toast.success("Pedido eliminado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 md:mb-8">
        <div>
          <p className="text-[10px] text-[#5A5D6E] uppercase tracking-[0.2em] font-medium">Historial</p>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight mt-2">Pedidos</h1>
          <p className="text-sm text-[#5A5D6E] mt-2">Pedidos enviados a proveedores.</p>
        </div>
        <Button onClick={() => navigate("/pedidos/nuevo")} className="bg-[#002FA7] hover:bg-[#002FA7]/90 rounded-sm w-full md:w-auto" data-testid="order-new-button">
          <Plus className="w-4 h-4 mr-2" /> Nuevo pedido
        </Button>
      </div>

      <div className="flex gap-1 mb-4 border-b border-[#E2E4E9] overflow-x-auto">
        {[
          { k: "all", label: "Todos" },
          { k: "draft", label: "Borradores" },
          { k: "sent", label: "Enviados" },
          { k: "failed", label: "Fallidos" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setFilter(t.k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${filter === t.k ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-[#5A5D6E] hover:text-[#0D0E15]"}`}
            data-testid={`filter-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-[#E2E4E9] rounded-sm p-3 md:p-4 mb-4 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:items-end" data-testid="orders-filters">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E]">Proveedor</Label>
          <Select value={supplierFilter} onValueChange={setSupplierFilter}>
            <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="orders-filter-supplier">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los proveedores</SelectItem>
              {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 sm:flex gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E]">Desde</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-sm h-9 mt-1" data-testid="orders-filter-date-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E]">Hasta</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-sm h-9 mt-1" data-testid="orders-filter-date-to" />
          </div>
        </div>
        {hasActiveFilters && (
          <Button variant="outline" size="sm" onClick={clearFilters} className="rounded-sm h-9 w-full sm:w-auto" data-testid="orders-filter-clear">
            <X className="w-3.5 h-3.5 mr-1" /> Limpiar filtros
          </Button>
        )}
        <div className="text-xs text-[#5A5D6E] sm:ml-auto">
          {filtered.length} de {items.length} pedido{items.length !== 1 ? "s" : ""}
        </div>
      </div>

      <div className="bg-white border border-[#E2E4E9] rounded-sm overflow-x-auto">
        {filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Inbox className="w-12 h-12 text-[#E2E4E9] mx-auto" strokeWidth={1.25} />
            <p className="text-sm text-[#5A5D6E] mt-3">Sin pedidos</p>
          </div>
        ) : (
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-[#F7F7F9] border-b border-[#E2E4E9]">
              <tr>
                <th className="text-left p-3 text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Número</th>
                <th className="text-left p-3 text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Proveedor</th>
                <th className="text-left p-3 text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Items</th>
                <th className="text-left p-3 text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Estado</th>
                <th className="text-left p-3 text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Fecha</th>
                <th className="w-44 p-3 text-right text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr key={o.id} className="border-b border-[#E2E4E9] hover:bg-[#F7F7F9]" data-testid={`order-row-${o.id}`}>
                  <td className="p-3 font-mono text-xs">{o.order_number}</td>
                  <td className="p-3">
                    <p className="font-medium">{o.supplier_name}</p>
                    <p className="text-xs text-[#5A5D6E]">{o.supplier_email}</p>
                  </td>
                  <td className="p-3 font-mono text-xs">{o.items?.length || 0}</td>
                  <td className="p-3">
                    {o.status === "sent" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-[#16A34A]/10 text-[#16A34A]">
                        <CheckCircle2 className="w-3 h-3" /> Enviado
                      </span>
                    ) : o.status === "draft" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-[#5A5D6E]/10 text-[#5A5D6E]">
                        <FileClock className="w-3 h-3" /> Borrador
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-[#D32F2F]/10 text-[#D32F2F]" title={o.error_message}>
                        <AlertCircle className="w-3 h-3" /> Falló
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-[#5A5D6E]">{new Date(o.created_at).toLocaleString("es-AR")}</td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-3">
                      {o.status === "draft" && (
                        <>
                          <button onClick={() => navigate(`/pedidos/${o.id}/editar`)} className="text-xs text-[#5A5D6E] hover:text-[#002FA7] flex items-center gap-1" data-testid={`order-edit-${o.id}`}>
                            <FileEdit className="w-3 h-3" /> Editar
                          </button>
                          <button onClick={() => setConfirmSendDraft(o)} className="text-xs text-[#002FA7] hover:underline flex items-center gap-1" data-testid={`order-send-now-${o.id}`}>
                            <Send className="w-3 h-3" /> Enviar
                          </button>
                        </>
                      )}
                      {o.status === "sent" && (
                        <button onClick={() => resend(o.id)} className="text-xs text-[#002FA7] hover:underline flex items-center gap-1" data-testid={`order-resend-${o.id}`}>
                          <RefreshCw className="w-3 h-3" /> Reenviar
                        </button>
                      )}
                      {o.status === "failed" && (
                        <button onClick={() => resend(o.id)} className="text-xs text-[#002FA7] hover:underline flex items-center gap-1" data-testid={`order-retry-${o.id}`}>
                          <RefreshCw className="w-3 h-3" /> Reintentar
                        </button>
                      )}
                      <button onClick={() => setConfirmDelete(o)} className="text-xs text-[#D32F2F] hover:bg-[#D32F2F]/10 p-1 rounded-sm" title="Eliminar" data-testid={`order-delete-${o.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Eliminar pedido</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete && (
                <>
                  ¿Estás seguro de eliminar el pedido <span className="font-mono text-xs">{confirmDelete.order_number}</span> de <strong>{confirmDelete.supplier_name}</strong>?
                  <br />Esta acción no se puede deshacer.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-[#D32F2F] hover:bg-[#D32F2F]/90 rounded-sm" data-testid="confirm-delete-button">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmSendDraft} onOpenChange={(o) => !o && setConfirmSendDraft(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="confirm-send-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Enviar borrador</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmSendDraft && (
                <>Se enviará el pedido a <strong>{confirmSendDraft.supplier_name}</strong> ({confirmSendDraft.supplier_email}).</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doSendDraft} className="bg-[#002FA7] hover:bg-[#002FA7]/90 rounded-sm" data-testid="confirm-send-button">
              Enviar ahora
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function NewOrder() {
  const [params] = useSearchParams();
  const { id: editId } = useParams();
  const tplId = params.get("template");
  const navigate = useNavigate();
  const isEditing = !!editId;

  const [suppliers, setSuppliers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState(tplId || "");
  const [supplierId, setSupplierId] = useState("");
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [smtpReady, setSmtpReady] = useState(true);
  const [loadedDraft, setLoadedDraft] = useState(false);
  const { config } = useAppConfig();
  const companyName = config?.company_name || "";

  useEffect(() => {
    api.listSuppliers().then(setSuppliers);
    api.listTemplates().then(setTemplates);
    api.getSmtp().then((c) => setSmtpReady(!!c));
  }, []);

  // Load draft order if editing
  useEffect(() => {
    if (!editId) return;
    api.getOrder(editId).then((o) => {
      setSupplierId(o.supplier_id);
      setItems(o.items || []);
      setNotes(o.notes || "");
      setSubject(o.subject || "");
      setBody(o.body || "");
      setSelectedTpl(o.template_id || "");
      setLoadedDraft(true);
    }).catch(() => toast.error("No se pudo cargar el borrador"));
  }, [editId]);

  const loadTemplate = async (id, { confirm = true } = {}) => {
    if (!id) return;
    if (confirm && items.length > 0) {
      if (!window.confirm("¿Reemplazar los items actuales con los de la plantilla?")) return;
    }
    try {
      const t = await api.getTemplate(id);
      setSupplierId(t.supplier_id);
      // Sort items alphabetically by description (case-insensitive, locale aware)
      const sortedItems = [...(t.items || [])].sort((a, b) =>
        (a.description || "").localeCompare(b.description || "", "es", { sensitivity: "base" })
      );
      setItems(sortedItems);
      setNotes(t.notes || "");
      setSelectedTpl(id);
      toast.success(`Plantilla "${t.name}" cargada`);
    } catch (e) {
      toast.error("No se pudo cargar la plantilla");
    }
  };

  // Load template from URL param on first mount
  useEffect(() => {
    if (tplId && !editId) loadTemplate(tplId, { confirm: false });
    // eslint-disable-next-line
  }, [tplId]);

  const sup = suppliers.find((s) => s.id === supplierId);
  const supplierTemplates = templates.filter((t) => t.supplier_id === supplierId);

  const onSupplierChange = async (newSupplierId) => {
    setSupplierId(newSupplierId);
    setSelectedTpl("");
    const newSup = suppliers.find((s) => s.id === newSupplierId);
    if (newSup?.default_template_id && items.length === 0) {
      await loadTemplate(newSup.default_template_id, { confirm: false });
    }
  };

  useEffect(() => {
    if (sup && !subject && !loadedDraft) setSubject(companyName ? `Pedido ${companyName}` : `Pedido`);
    if (sup && !body && !loadedDraft) setBody(`Estimado/a ${sup.contact_name || sup.name},\n\nLes envío el siguiente pedido. Quedo atento/a a la confirmación.\n\nSaludos cordiales.`);
  }, [sup, loadedDraft, companyName]);  // eslint-disable-line

  const addEmpty = () => setItems([...items, { reference: "", description: "", quantity: 1, unit: "u" }]);
  const updateItem = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const validateBasic = () => {
    if (!supplierId) { toast.error("Seleccioná un proveedor"); return false; }
    if (items.length === 0) { toast.error("Agregá al menos un item"); return false; }
    return true;
  };

  const buildPayload = (send) => ({
    supplier_id: supplierId,
    template_id: selectedTpl || null,
    items: items.map((it) => ({ ...it, quantity: parseFloat(it.quantity) || 0 })),
    notes, subject, body, send,
  });

  const onSaveDraft = async () => {
    if (!validateBasic()) return;
    setSavingDraft(true);
    try {
      if (isEditing) {
        await api.updateOrder(editId, {
          supplier_id: supplierId,
          template_id: selectedTpl || null,
          items: items.map((it) => ({ ...it, quantity: parseFloat(it.quantity) || 0 })),
          notes, subject, body,
        });
      } else {
        await api.createOrder(buildPayload(false));
      }
      toast.success("Borrador guardado");
      navigate("/pedidos");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingDraft(false);
    }
  };

  const onSend = async () => {
    if (!validateBasic()) return;
    setSending(true);
    try {
      if (isEditing) {
        // save draft updates first, then send
        await api.updateOrder(editId, {
          supplier_id: supplierId,
          template_id: selectedTpl || null,
          items: items.map((it) => ({ ...it, quantity: parseFloat(it.quantity) || 0 })),
          notes, subject, body,
        });
        await api.sendOrder(editId);
      } else {
        await api.createOrder(buildPayload(true));
      }
      toast.success("Pedido enviado correctamente");
      navigate("/pedidos");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <div className="flex items-center gap-2 text-xs text-[#5A5D6E] mb-2">
        <span className="cursor-pointer hover:text-[#002FA7]" onClick={() => navigate("/pedidos")}>Pedidos</span>
        <ChevronRight className="w-3 h-3" />
        <span>{isEditing ? "Editar borrador" : "Nuevo"}</span>
      </div>
      <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-6 md:mb-8">{isEditing ? "Editar borrador" : "Nuevo pedido"}</h1>

      {!smtpReady && (
        <div className="bg-[#F57F17]/10 border border-[#F57F17]/30 p-3 rounded-sm mb-6 text-sm text-[#0D0E15]" data-testid="smtp-warning">
          <strong>SMTP sin configurar.</strong> Configurá tus credenciales en <a href="/configuracion" className="text-[#002FA7] underline">Configuración</a> antes de enviar.
        </div>
      )}

      <div className="bg-white border border-[#E2E4E9] p-4 md:p-6 rounded-sm mb-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium mb-4">1. Proveedor y plantilla</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Proveedor *</Label>
            <Select value={supplierId} onValueChange={onSupplierChange}>
              <SelectTrigger data-testid="order-supplier-select"><SelectValue placeholder="Seleccionar proveedor" /></SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} — {s.email}</SelectItem>)}
              </SelectContent>
            </Select>
            {sup?.default_template_id && (
              <p className="text-xs text-[#5A5D6E] mt-1">Tiene plantilla por defecto asignada.</p>
            )}
          </div>
          <div>
            <Label>Plantilla {supplierId ? "" : <span className="text-[#5A5D6E] normal-case">(elegí proveedor primero)</span>}</Label>
            <div className="flex gap-2 items-center">
              <div className="flex-1">
                <Select
                  value={selectedTpl || "__none__"}
                  onValueChange={(v) => v === "__none__" ? setSelectedTpl("") : loadTemplate(v)}
                  disabled={!supplierId}
                >
                  <SelectTrigger data-testid="order-template-select">
                    <SelectValue placeholder={!supplierId ? "—" : (supplierTemplates.length === 0 ? "Sin plantillas para este proveedor" : "Seleccionar plantilla")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin plantilla</SelectItem>
                    {supplierTemplates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} · {t.items?.length || 0} items
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
        <p className="text-xs text-[#5A5D6E] mt-3">Al elegir un proveedor con plantilla por defecto, se cargan los items automáticamente. Podés editarlos antes de enviar.</p>
      </div>

      <div className="bg-white border border-[#E2E4E9] p-4 md:p-6 rounded-sm mb-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium mb-4">2. Mensaje del email</p>
        <div>
          <Label>Asunto</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="order-subject-input" />
        </div>
        <div className="mt-4">
          <Label>Mensaje</Label>
          <Textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} data-testid="order-body-input" />
        </div>
      </div>

      <div className="bg-white border border-[#E2E4E9] rounded-sm mb-6">
        <div className="flex items-center justify-between p-4 border-b border-[#E2E4E9]">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Items del pedido</p>
          <Button size="sm" onClick={addEmpty} className="bg-[#002FA7] hover:bg-[#002FA7]/90 rounded-sm h-8">
            <Plus className="w-3.5 h-3.5 mr-1" /> Agregar
          </Button>
        </div>
        {items.length === 0 ? (
          <div className="py-12 text-center text-sm text-[#5A5D6E]">Sin items</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-[#F7F7F9] border-b border-[#E2E4E9]">
              <tr>
                <th className="text-left p-2 text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] w-24 font-medium">Ref.</th>
                <th className="text-left p-2 text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Descripción</th>
                <th className="text-left p-2 text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] w-20 font-medium">Cant.</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-b border-[#E2E4E9]">
                  <td className="p-1.5">
                    <Input
                      value={it.reference}
                      onChange={(e) => updateItem(i, "reference", e.target.value)}
                      className="h-8 rounded-sm font-mono text-xs"
                      data-testid={`order-item-ref-${i}`}
                    />
                  </td>
                  <td className="p-1.5"><Input value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} className="h-8 rounded-sm" data-testid={`order-item-desc-${i}`} /></td>
                  <td className="p-1.5">
                    <Input
                      type="number"
                      min="0"
                      max="999"
                      step="1"
                      value={it.quantity}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "") { updateItem(i, "quantity", ""); return; }
                        const n = Math.max(0, Math.min(999, parseInt(v) || 0));
                        updateItem(i, "quantity", String(n));
                      }}
                      className="h-8 rounded-sm text-center"
                      data-testid={`order-item-qty-${i}`}
                    />
                  </td>
                  <td className="p-1.5">
                    <button onClick={() => removeItem(i)} className="p-1.5 hover:bg-[#D32F2F]/10 text-[#D32F2F] rounded-sm">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
        <Button variant="outline" onClick={() => navigate("/pedidos")} className="rounded-sm">Cancelar</Button>
        <Button
          variant="outline"
          onClick={onSaveDraft}
          disabled={savingDraft || sending}
          className="rounded-sm"
          data-testid="order-save-draft-button"
        >
          <Save className="w-4 h-4 mr-2" /> {savingDraft ? "Guardando…" : (isEditing ? "Guardar cambios" : "Guardar borrador")}
        </Button>
        <Button onClick={onSend} disabled={sending || savingDraft || !smtpReady} className="bg-[#002FA7] hover:bg-[#002FA7]/90 rounded-sm" data-testid="order-send-button">
          <Send className="w-4 h-4 mr-2" /> {sending ? "Enviando…" : (isEditing ? "Enviar ahora" : "Enviar pedido")}
        </Button>
      </div>
    </div>
  );
}
